import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  ConnectionResponseSchema,
  ConnectionsResponseSchema,
  ConnectionTokenResponseSchema,
} from "@forgepoint/shared";
import type {
  ConnectionProviderKey,
  ConnectionTokenResponse,
  PublicConnection,
} from "@forgepoint/shared";

interface ConnectionState {
  isLoadingGet: boolean;
  isLoadingCreate: boolean;
  isLoadingFinalize: boolean;
  getError: string | null;
  createError: string | null;
  finalizeError: string | null;
}

interface ConnectionsStore {
  connections: Map<string, PublicConnection>;
  connectionStates: Map<string, ConnectionState>;
  selectedConnectionId: string | null;

  // Fetchers
  fetchConnectionsByApp: (appId: string) => Promise<void>;
  fetchConnection: (id: string) => Promise<void>;

  // Token generation for connections
  generateConnectionToken: (
    providerId: ConnectionProviderKey,
    appId: string,
    options?: { redirectUri?: string },
  ) => Promise<ConnectionTokenResponse | null>;

  // Finalize connection after OAuth
  finalizeConnection: (input: {
    connectionProviderId: ConnectionProviderKey;
    appId: string;
    authId?: string;
    credentials?: Record<string, unknown>;
    config?: Record<string, unknown>;
    requiresEgressProxy?: boolean;
    name?: string;
    state?: string;
  }) => Promise<PublicConnection | null>;

  // Getters
  getConnectionsByApp: (appId: string) => PublicConnection[];
  getConnectionById: (id: string) => PublicConnection | undefined;
  getSelectedConnection: () => PublicConnection | undefined;

  // Setters
  setSelectedConnection: (id: string | null) => void;
  clearConnectionError: (appId: string) => void;
}

export const useConnectionsStore = create<ConnectionsStore>((set, get) => ({
  connections: new Map<string, PublicConnection>(),
  connectionStates: new Map(),
  selectedConnectionId: null,

  fetchConnectionsByApp: async (appId: string) => {
    const currentState = get().connectionStates.get(appId) || {
      isLoadingGet: false,
      isLoadingCreate: false,
      isLoadingFinalize: false,
      getError: null,
      createError: null,
      finalizeError: null,
    };

    set({
      connectionStates: new Map(get().connectionStates).set(appId, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      }),
    });

    try {
      const response = await fetchApi(`connections/app/${appId}`, "GET");

      if (response.ok) {
        const parsed = ConnectionsResponseSchema.safeParse(response.data);

        if (parsed.success) {
          const currentConnections = new Map(get().connections);

          parsed.data.connections.forEach((connection) => {
            currentConnections.set(connection.id, connection);
          });

          set({
            connections: currentConnections,
            connectionStates: new Map(get().connectionStates).set(appId, {
              ...currentState,
              isLoadingGet: false,
              getError: null,
            }),
          });
          return;
        }

        captureException(parsed.error);

        set({
          connectionStates: new Map(get().connectionStates).set(appId, {
            ...currentState,
            isLoadingGet: false,
            getError: "Failed to parse connections",
          }),
        });

        return;
      } else {
        const errorData = response.data as { error?: string } | undefined;
        const errorMessage = errorData?.error || "Failed to fetch connections";

        set({
          connectionStates: new Map(get().connectionStates).set(appId, {
            ...currentState,
            isLoadingGet: false,
            getError: errorMessage,
          }),
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch connections";
      captureException(error as Error);

      set({
        connectionStates: new Map(get().connectionStates).set(appId, {
          ...currentState,
          isLoadingGet: false,
          getError: errorMessage,
        }),
      });
    }
  },

  fetchConnection: async (id: string) => {
    try {
      const response = await fetchApi(`connections/${id}`, "GET");

      if (response.ok) {
        const parsed = ConnectionResponseSchema.safeParse(response.data);

        if (!parsed.success) {
          captureException(parsed.error);
          return;
        }

        const currentConnections = new Map(get().connections);
        currentConnections.set(id, parsed.data.connection);

        set({ connections: currentConnections });
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  generateConnectionToken: async (
    providerId: ConnectionProviderKey,
    appId: string,
    options,
  ) => {
    try {
      // Call the connection token endpoint
      const response = await fetchApi("connections/token", "POST", {
        body: {
          providerId,
          appId,
          redirectUri: options?.redirectUri,
        },
      });

      if (response.ok) {
        const parsed = ConnectionTokenResponseSchema.safeParse(response.data);

        if (!parsed.success) {
          captureException(parsed.error);
          return null;
        }

        return parsed.data;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  finalizeConnection: async ({
    connectionProviderId,
    authId,
    appId,
    credentials,
    config,
    requiresEgressProxy,
    name,
    state,
  }) => {
    const currentState = get().connectionStates.get(appId) || {
      isLoadingGet: false,
      isLoadingCreate: false,
      isLoadingFinalize: false,
      getError: null,
      createError: null,
      finalizeError: null,
    };

    set({
      connectionStates: new Map(get().connectionStates).set(appId, {
        ...currentState,
        isLoadingFinalize: true,
        finalizeError: null,
      }),
    });

    try {
      const response = await fetchApi("connections/finalize", "POST", {
        body: {
          connectionProviderId,
          authId,
          appId,
          credentials,
          config,
          requiresEgressProxy,
          name,
          state,
        },
      });

      if (response.ok) {
        const parsed = ConnectionResponseSchema.safeParse(response.data);

        if (!parsed.success) {
          captureException(parsed.error);

          set({
            connectionStates: new Map(get().connectionStates).set(appId, {
              ...currentState,
              isLoadingFinalize: false,
              finalizeError: "Failed to parse connection",
            }),
          });

          return null;
        }

        const currentConnections = new Map(get().connections);
        currentConnections.set(
          parsed.data.connection.id,
          parsed.data.connection,
        );

        set({
          connections: currentConnections,
          connectionStates: new Map(get().connectionStates).set(appId, {
            ...currentState,
            isLoadingFinalize: false,
            finalizeError: null,
          }),
          selectedConnectionId: parsed.data.connection.id,
        });

        return parsed.data.connection;
      } else {
        const errorData = response.data as { error?: string } | undefined;
        const errorMessage =
          errorData?.error || "Failed to finalize connection";

        set({
          connectionStates: new Map(get().connectionStates).set(appId, {
            ...currentState,
            isLoadingFinalize: false,
            finalizeError: errorMessage,
          }),
        });

        return null;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to finalize connection";
      captureException(error as Error);

      set({
        connectionStates: new Map(get().connectionStates).set(appId, {
          ...currentState,
          isLoadingFinalize: false,
          finalizeError: errorMessage,
        }),
      });

      return null;
    }
  },

  getConnectionsByApp: (appId: string) => {
    const connections = Array.from(get().connections.values());
    return connections.filter((conn) => conn.appId === appId);
  },

  getConnectionById: (id: string) => {
    return get().connections.get(id);
  },

  getSelectedConnection: () => {
    const selectedId = get().selectedConnectionId;
    return selectedId ? get().connections.get(selectedId) : undefined;
  },

  setSelectedConnection: (id: string | null) => {
    set({ selectedConnectionId: id });
  },

  clearConnectionError: (appId: string) => {
    const currentState = get().connectionStates.get(appId);
    if (currentState) {
      set({
        connectionStates: new Map(get().connectionStates).set(appId, {
          ...currentState,
          getError: null,
          createError: null,
          finalizeError: null,
        }),
      });
    }
  },
}));
